//src/pages/gro/ActionPlanModelFormPage.tsx
import { useEffect, useState } from "react";
import { ArrowLeft, Loader2, Save } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";

import type { GroCrudScope } from "@/lib/groCrudScope";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  useActionPlanModelDetail,
  useCreateActionPlanModel,
  useUpdateActionPlanModel,
} from "@/hooks/useActionPlanModels";
import type { ActionPlanModelCreateIn } from "@/types/actionPlanModels";

const EMPTY_FORM: ActionPlanModelCreateIn = {
  code: "",
  title: "",
  description: "",
  is_active: true,
};

function normalizeCode(value: string) {
  return value
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toUpperCase()
    .replace(/[^A-Z0-9\s]/g, " ")
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((part) => part.slice(0, 3))
    .join("")
    .slice(0, 30);
}

type ActionPlanModelFormPageProps = {
  scope?: GroCrudScope;
  basePath?: string;
  entityLabel?: string;
};

export default function ActionPlanModelFormPage({
  scope = "client",
  basePath = "/gro/action-plan-models",
  entityLabel = "Plano de Ação",
}: ActionPlanModelFormPageProps) {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isCreating = !id;
  const detailQ = useActionPlanModelDetail(id, scope);
  const createMut = useCreateActionPlanModel(scope);
  const updateMut = useUpdateActionPlanModel(scope);

  const [form, setForm] = useState<ActionPlanModelCreateIn>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [codeTouched, setCodeTouched] = useState(false);

  useEffect(() => {
    if (!detailQ.data) return;
    setForm({
      code: detailQ.data.code,
      title: detailQ.data.title,
      description: detailQ.data.description || "",
      is_active: detailQ.data.is_active,
    });
    setCodeTouched(true);
  }, [detailQ.data]);

  function updateField<K extends keyof ActionPlanModelCreateIn>(field: K, value: ActionPlanModelCreateIn[K]) {
    setForm((prev) => ({ ...prev, [field]: value }));
  }

  function handleTitleChange(value: string) {
    setForm((prev) => ({
      ...prev,
      title: value,
      code: codeTouched ? prev.code : normalizeCode(value),
    }));
  }

  async function handleSave() {
    if (!form.title.trim()) {
      toast.error("Informe o título do plano.");
      return;
    }
    if (!form.code.trim()) {
      toast.error("Informe o código do plano.");
      return;
    }
    setSaving(true);
    try {
      if (id) {
        await updateMut.mutateAsync({ id, body: form });
        toast.success("Plano de ação atualizado com sucesso.");
      } else {
        const created = await createMut.mutateAsync(form);
        toast.success("Plano de ação criado com sucesso.");
        navigate(`${basePath}/${created.id}`);
        return;
      }
      navigate(`${basePath}/${id}`);
    } catch (e: any) {
      toast.error(e?.message || "Erro ao salvar plano de ação.");
    } finally {
      setSaving(false);
    }
  }

  if (!isCreating && detailQ.isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="pb-10">
      <div className="sticky top-14 z-20 -mx-1 mb-6 border-b bg-background/95 px-1 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="flex flex-col gap-3 py-4 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate(basePath)}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold tracking-tight">{isCreating ? `Novo ${entityLabel}` : `Editar ${entityLabel}`}</h1>
              <p className="text-sm text-muted-foreground">Cadastro-base de planos reutilizáveis, sem vínculo direto com risco no cabeçalho.</p>
            </div>
          </div>
          <div className="flex items-center gap-2 self-end md:self-auto">
            <Button variant="outline" onClick={() => navigate(basePath)} disabled={saving}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Voltar
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
              Salvar
            </Button>
          </div>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Dados do plano</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <Label>Título *</Label>
            <Input className="mt-1" value={form.title} onChange={(e) => handleTitleChange(e.target.value)} placeholder="Ex.: Programa de mitigação psicossocial" />
          </div>
          <div>
            <Label>Código *</Label>
            <Input className="mt-1" value={form.code} onChange={(e) => { setCodeTouched(true); updateField("code", normalizeCode(e.target.value)); }} placeholder="Ex.: PROMITPSI" />
          </div>
          <div className="md:col-span-2">
            <Label>Descrição</Label>
            <Textarea className="mt-1 min-h-[140px]" value={form.description ?? ""} onChange={(e) => updateField("description", e.target.value)} placeholder="Descreva o objetivo e o uso recomendado deste plano de ação." />
          </div>
          <div>
            <Label>Status</Label>
            <Select value={form.is_active ? "active" : "inactive"} onValueChange={(value) => updateField("is_active", value === "active") }>
              <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="active">Ativo</SelectItem>
                <SelectItem value="inactive">Inativo</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
